import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  ThemeProvider,
  Toaster,
  TooltipProvider,
  useSidebar,
  useTheme,
} from "@qualtio-workhub/ui";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { Plus } from "lucide-react";
import {
  type ComponentType,
  type CSSProperties,
  lazy,
  type ReactNode,
  Suspense,
  useEffect,
  useState,
} from "react";
import { subscribeToInboxChanges } from "../features/inbox/inbox-events";
import { CreateIssueModal } from "../features/issues/create-issue-modal";
import { HomePage } from "../features/issues/home-page";
import { IssueDetailPage } from "../features/issues/issue-detail-page";
import { IssueListPage } from "../features/issues/issue-list-page";
import { listInbox, listIssues } from "../shared/api/client";
import { useWorkhubQuery } from "../shared/api/hooks";
import { AuthProvider, useAuth } from "../shared/auth/auth-provider";
import { I18nProvider, useI18n } from "../shared/i18n/provider";
import { BrandMark } from "./brand";
import { type AppRoutePath, getVisibleNavItems, navGroups } from "./navigation";
import { LoadingPage, LoginPage, TopbarControls, useCommandPaletteShortcut } from "./pages";

const AnalyticsPage = lazy(() =>
  import("../features/analytics/analytics-page").then((module) => ({
    default: module.AnalyticsPage,
  })),
);
const AgentRunDetailPage = lazy(() =>
  import("../features/agents/agent-run-detail-page").then((module) => ({
    default: module.AgentRunDetailPage,
  })),
);
const AgentsPage = lazy(() =>
  import("../features/agents/agents-page").then((module) => ({ default: module.AgentsPage })),
);
const BoardPage = lazy(() =>
  import("../features/board/board-page").then((module) => ({ default: module.BoardPage })),
);
const InboxPage = lazy(() =>
  import("../features/inbox/inbox-page").then((module) => ({ default: module.InboxPage })),
);
const TriagePage = lazy(() =>
  import("../features/triage/triage-page").then((module) => ({ default: module.TriagePage })),
);
const ProductsPage = lazy(() =>
  import("../features/context/context-pages").then((module) => ({ default: module.ProductsPage })),
);
const ClientsPage = lazy(() =>
  import("../features/context/context-pages").then((module) => ({ default: module.ClientsPage })),
);
const ProjectsPage = lazy(() =>
  import("../features/context/context-pages").then((module) => ({ default: module.ProjectsPage })),
);
const ResearchTracksPage = lazy(() =>
  import("../features/context/context-pages").then((module) => ({
    default: module.ResearchTracksPage,
  })),
);
const ChaptersPage = lazy(() =>
  import("../features/context/context-pages").then((module) => ({ default: module.ChaptersPage })),
);
const CyclesPage = lazy(() =>
  import("../features/planning/planning-pages").then((module) => ({ default: module.CyclesPage })),
);
const RoadmapPage = lazy(() =>
  import("../features/planning/planning-pages").then((module) => ({ default: module.RoadmapPage })),
);
const ApiKeysPage = lazy(() =>
  import("../features/settings/api-keys/api-keys-page").then((module) => ({
    default: module.ApiKeysPage,
  })),
);
const UsersSettingsPage = lazy(() =>
  import("../features/settings/users/users-settings-page").then((module) => ({
    default: module.UsersSettingsPage,
  })),
);
const SlackSettingsPage = lazy(() =>
  import("../features/settings/slack/slack-settings-page").then((module) => ({
    default: module.SlackSettingsPage,
  })),
);
const MaintenancePage = lazy(() =>
  import("../features/settings/maintenance/maintenance-page").then((module) => ({
    default: module.MaintenancePage,
  })),
);
const GithubSettingsPage = lazy(() =>
  import("../features/settings/github/github-settings-page").then((module) => ({
    default: module.GithubSettingsPage,
  })),
);

function RootLayout() {
  return (
    <ThemeProvider>
      <TooltipProvider>
        <I18nProvider>
          <AuthProvider>
            <Outlet />
            <Toaster position="bottom-right" />
          </AuthProvider>
        </I18nProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}

function ProtectedLayout() {
  const auth = useAuth();

  if (auth.status === "loading") {
    return <LoadingPage />;
  }

  if (auth.status === "anonymous") {
    return <Navigate to="/login" />;
  }

  return (
    <SidebarProvider
      className="workhub-shell"
      style={
        {
          "--sidebar-width": "248px",
          "--sidebar-width-icon": "52px",
        } as CSSProperties
      }
    >
      <AuthenticatedLayout />
    </SidebarProvider>
  );
}

function AuthenticatedLayout() {
  const auth = useAuth();
  const theme = useTheme();
  const sidebar = useSidebar();
  const { t } = useI18n();
  const router = useRouter();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [commandOpen, setCommandOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const issuesQuery = useWorkhubQuery(listIssues);
  const inboxQuery = useWorkhubQuery(listInbox);
  const unreadInboxCount = inboxQuery.data?.length ?? 0;
  const visibleNavItems = getVisibleNavItems(auth.user?.role);

  useCommandPaletteShortcut(() => setCommandOpen(true));

  useEffect(() => {
    if (auth.status !== "authenticated") {
      return undefined;
    }

    return subscribeToInboxChanges(() => {
      void inboxQuery.refetch();
    });
  }, [auth.status, inboxQuery.refetch]);

  return (
    <>
      <button
        className="skip-link"
        type="button"
        onClick={() => document.getElementById("main-content")?.focus()}
      >
        {t("shell.skipToContent")}
      </button>
      <Sidebar
        aria-label={t("shell.navigation")}
        className="workhub-sidebar"
        collapsible="icon"
        mobileCloseLabel={t("common.close")}
        mobileDescription={`${t("app.name")} · ${t("shell.navigation")}`}
        mobileTitle={t("shell.navigation")}
      >
        <SidebarHeader>
          <div className="sidebar-brand">
            <BrandMark />
            <span className="group-data-[collapsible=icon]:hidden">{t("app.name")}</span>
          </div>
        </SidebarHeader>
        <SidebarContent>
          <nav aria-label={t("shell.navigation")} className="workhub-sidebar-nav">
            {navGroups.map((group) => (
              <SidebarGroup key={group.id}>
                <SidebarGroupLabel>{t(group.labelKey)}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {visibleNavItems
                      .filter((item) => item.group === group.id)
                      .map((item) => {
                        const active = isNavItemActive(pathname, item.path);
                        return (
                          <SidebarMenuItem key={item.path}>
                            <SidebarMenuButton asChild isActive={active} tooltip={t(item.labelKey)}>
                              <a
                                aria-current={active ? "page" : undefined}
                                href={item.path}
                                onClick={(event) => {
                                  if (
                                    event.button !== 0 ||
                                    event.metaKey ||
                                    event.ctrlKey ||
                                    event.shiftKey ||
                                    event.altKey
                                  ) {
                                    return;
                                  }
                                  event.preventDefault();
                                  sidebar.setOpenMobile(false);
                                  void router.navigate({ to: item.path });
                                }}
                              >
                                <item.icon aria-hidden="true" />
                                <span>{t(item.labelKey)}</span>
                              </a>
                            </SidebarMenuButton>
                            {item.path === "/inbox" && unreadInboxCount > 0 ? (
                              <SidebarMenuBadge
                                className="nav-badge"
                                title={`${unreadInboxCount} ${t("inbox.unreadTab")}`}
                              >
                                {unreadInboxCount > 9 ? "9+" : unreadInboxCount}
                              </SidebarMenuBadge>
                            ) : null}
                          </SidebarMenuItem>
                        );
                      })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          </nav>
        </SidebarContent>
        <SidebarFooter>
          <div className="sidebar-footer group-data-[collapsible=icon]:hidden">
            <span className="muted-copy">{t("app.workspace")}</span>
          </div>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SidebarInset className="shell-main" id="main-content" tabIndex={-1}>
        <header className="topbar">
          <div className="topbar-context">
            <SidebarTrigger aria-label={t("shell.navigation")} />
            <span className="muted-copy">{t("app.workspace")}</span>
          </div>
          <TopbarControls
            isDark={theme.resolvedMode === "dark"}
            onCommandOpen={() => setCommandOpen(true)}
            onThemeToggle={theme.toggleTheme}
          />
        </header>
        <Outlet />
      </SidebarInset>
      <CommandDialog
        description={t("command.placeholder")}
        open={commandOpen}
        title={t("command.title")}
        onOpenChange={setCommandOpen}
      >
        <Command>
          <CommandInput aria-label={t("command.search")} placeholder={t("command.placeholder")} />
          <CommandList>
            <CommandEmpty>{t("command.empty")}</CommandEmpty>
            <CommandGroup heading={t("work.meta")}>
              <CommandItem
                value={`${t("command.createIssue")} new issue`}
                onSelect={() => {
                  setCommandOpen(false);
                  setCreateOpen(true);
                }}
              >
                <Plus aria-hidden="true" />
                {t("command.createIssue")}
              </CommandItem>
            </CommandGroup>
            {navGroups.map((group) => (
              <CommandGroup heading={t(group.labelKey)} key={group.id}>
                {visibleNavItems
                  .filter((item) => item.group === group.id)
                  .map((item) => (
                    <CommandItem
                      key={item.path}
                      value={`${t(item.labelKey)} ${item.path} ${t(group.labelKey)}`}
                      onSelect={() => {
                        setCommandOpen(false);
                        void router.navigate({ to: item.path });
                      }}
                    >
                      <item.icon aria-hidden="true" />
                      {t(item.labelKey)}
                    </CommandItem>
                  ))}
              </CommandGroup>
            ))}
            <CommandGroup heading={t("nav.issues")}>
              {(issuesQuery.data ?? []).map((issue) => (
                <CommandItem
                  key={issue.id}
                  value={`${issue.key} ${issue.title} ${t("nav.issues")}`}
                  onSelect={() => {
                    setCommandOpen(false);
                    void router.navigate({
                      to: "/issues/$issueKey",
                      params: { issueKey: issue.key },
                    });
                  }}
                >
                  <span className="issue-key">{issue.key}</span>
                  <span>{issue.title}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </CommandDialog>
      <CreateIssueModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(issueKey) => {
          void issuesQuery.refetch();
          void router.navigate({ to: "/issues/$issueKey", params: { issueKey } });
        }}
      />
    </>
  );
}

function isNavItemActive(pathname: string, itemPath: string): boolean {
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

const rootRoute = createRootRoute({
  component: RootLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  component: ProtectedLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: () => <Navigate to="/my-work" />,
});

function featureRoute(path: AppRoutePath, component: () => ReactNode) {
  return createRoute({
    getParentRoute: () => appRoute,
    path,
    component,
  });
}

function lazyRoute(path: AppRoutePath, Component: ComponentType) {
  return featureRoute(path, () => (
    <Suspense fallback={<LoadingPage />}>
      <Component />
    </Suspense>
  ));
}

const issueDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/issues/$issueKey",
  component: IssueDetailPage,
});

const agentRunDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/agents/runs/$runId",
  component: () => (
    <Suspense fallback={<LoadingPage />}>
      <AgentRunDetailPage />
    </Suspense>
  ),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    indexRoute,
    featureRoute("/my-work", HomePage),
    lazyRoute("/inbox", InboxPage),
    lazyRoute("/triage", TriagePage),
    featureRoute("/issues", IssueListPage),
    issueDetailRoute,
    lazyRoute("/board", BoardPage),
    lazyRoute("/agents", AgentsPage),
    agentRunDetailRoute,
    lazyRoute("/products", ProductsPage),
    lazyRoute("/clients", ClientsPage),
    lazyRoute("/projects", ProjectsPage),
    lazyRoute("/research", ResearchTracksPage),
    lazyRoute("/chapters", ChaptersPage),
    lazyRoute("/cycles", CyclesPage),
    lazyRoute("/roadmap", RoadmapPage),
    lazyRoute("/analytics", AnalyticsPage),
    lazyRoute("/settings/api-keys", ApiKeysPage),
    lazyRoute("/settings/users", UsersSettingsPage),
    lazyRoute("/settings/maintenance", MaintenancePage),
    lazyRoute("/settings/slack", SlackSettingsPage),
    lazyRoute("/settings/github", GithubSettingsPage),
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function AppRouter() {
  return <RouterProvider router={router} />;
}
